import React, { useState, useEffect } from 'react';
import { Table, Typography, Spin, Alert, Button, Row, Col } from 'antd';
import * as XLSX from 'xlsx';
import PropTypes from 'prop-types';
import moment from 'moment';
import excelIcon from '../../assets/images/excel.png';

const { Text } = Typography;

const ExcelFilePreview = ({ fileUrl }) => {
    const [fileContent, setFileContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFallback, setShowFallback] = useState(false);

    const formatDate = (value) => {
        if (typeof value === 'number' && value > 0) {
            // Convert Excel serial date to JavaScript date
            const excelDate = value;
            const jsDate = new Date((excelDate - 25569) * 86400 * 1000);
            return moment(jsDate).format('MMM D, YYYY');
        }
        return value;
    };

    useEffect(() => {
        const fetchAndParseExcel = async () => {
            try {
                const response = await fetch(fileUrl);
                const arrayBuffer = await response.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                    header: 1,
                    raw: false, // Set to false to get formatted values
                    defval: '',
                    blankrows: true
                });
                
                if (jsonData.length > 0) {
                    const maxColumns = Math.max(...jsonData.map(row => row.length));
                    
                    const headerIndices = Array(maxColumns).fill(null).map((_, index) => {
                        const header = jsonData[0][index];
                        const headerStr = header ? String(header).trim() : '';
                        return { 
                            index, 
                            header: headerStr || `Column ${index + 1}`,
                            isVisible: !!headerStr
                        };
                    });
                    
                    const rows = jsonData.slice(1).map(row => {
                        return headerIndices.reduce((obj, { index, header }) => {
                            const value = row[index];
                            
                            if (value === null || value === undefined) {
                                obj[header] = '';
                            } else if (typeof value === 'object') {
                                obj[header] = JSON.stringify(value);
                            } else {
                                obj[header] = String(value).trim();
                            }
                            return obj;
                        }, {});
                    });
                    
                    setFileContent(rows);
                } else {
                    setFileContent([]);
                }
            } catch (error) {
                console.error('Error processing Excel content:', error);
                setError('Failed to parse Excel content: ' + error.message);
                setShowFallback(true);
            } finally {
                setLoading(false);
            }
        };

        fetchAndParseExcel();
    }, [fileUrl]);

    if (loading) {
        return <Spin size="small" />;
    }

    if (showFallback) {
        return (
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%',
                padding: '20px'
            }}>
                <img 
                    src={excelIcon} 
                    alt="Excel file" 
                    style={{ 
                        height: "50%", 
                        width: "50%", 
                        objectFit: "cover",
                        marginBottom: '20px'
                    }} 
                />
            </div>
        );
    }

    if (fileContent.length === 0) {
        return <Alert message="No content" description="The file appears to be empty." type="info" showIcon />;
    }

    const columns = Object.keys(fileContent[0]).map(key => ({
        title: key.startsWith('Column ') ? '' : key,
        dataIndex: key,
        key: key,
        width: 200,
        render: (text) => {
            const displayText = text === null || text === undefined ? '' : String(text);
            // Check if the text is a date value (numeric)
            if (!isNaN(text) && text > 0) {
                return <Text>{formatDate(text)}</Text>;
            }
            return <Text>{displayText}</Text>;
        },
    }));

    return (
        <div style={{ maxHeight: '80vh', overflow: 'auto' }}>
            <Table
                dataSource={fileContent}
                columns={columns}
                rowKey={(record, index) => index}
                pagination={false}
                size="small"
                scroll={{ x: 'max-content', y: '60vh' }}
                bordered
            />
        </div>
    );
};

ExcelFilePreview.defaultProps = {
    fileUrl: ''
};

ExcelFilePreview.propTypes = {
    fileUrl: PropTypes.string.isRequired
};

export default ExcelFilePreview; 